import { Card } from '@Infowijs-eng/component-library/components'
import { ArrowsLoop } from '@Infowijs-eng/component-library/icons'
import {
  useEffect, useMemo, useRef, useState,
} from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import ImporterLocationEntry from '../Importer/ImporterLocationEntry'
import useLASOrganisationalUnits from '../../hooks/data/useLASOrganisationalUnits'
import { OrganisationalUnit, PreferredLAS } from '../../types'
import makeGetCustomerProductOrganisationalUnits
  from '../../selectors/customerProducts/makeGetCustomerProductExporterOrganisationalUnits'

interface ExporterOrganisationalUnitSelectProps {
  customerProductName: string
  las: PreferredLAS
  organisationId: string
  onUpdateSelectedOrganisationalUnits: (units: OrganisationalUnit[]) => void
}

export default function ExporterOrganisationalUnitSelect(
  {
    customerProductName, las, organisationId, onUpdateSelectedOrganisationalUnits,
  }:
  ExporterOrganisationalUnitSelectProps,
) {
  const {
    data: organisationalUnits,
    isLoading,
    error,
  } = useLASOrganisationalUnits(customerProductName, las, organisationId)

  const touched = useRef(false)
  const { customerProductId } = useParams()

  const getCustomerProductExporterOrganisationalUnits = useMemo(makeGetCustomerProductOrganisationalUnits, [])

  const currentOrganisationalUnits = useSelector(
    (state) => getCustomerProductExporterOrganisationalUnits(state, { customerProductId }),
  )

  const [
    selectedOrganisationalUnits,
    setSelectedOrganisationalUnits] = useState<Map<string, OrganisationalUnit>>(new Map())

  const existingSelectedOrganisationalLookUpMap = useMemo(() => Object.fromEntries(
    currentOrganisationalUnits?.map((unit) => [unit.id, unit]) ?? [],
  ), [currentOrganisationalUnits])

  useEffect(() => {
    if (touched.current || isLoading || error) return

    const filteredOrganisationalUnits = currentOrganisationalUnits
      ?.filter((currentOrganisationalUnit) => organisationalUnits?.some(
        (organisationalUnit) => organisationalUnit.id === currentOrganisationalUnit.id,
      )) ?? []

    setSelectedOrganisationalUnits(new Map(filteredOrganisationalUnits.map((unit) => [unit.id, unit])))
    onUpdateSelectedOrganisationalUnits(filteredOrganisationalUnits)
  }, [currentOrganisationalUnits, organisationalUnits, isLoading, error])

  return (
    <div className="h-full">
      <div className="font-bold">Select locations to include in exporter</div>
      {/* @ts-expect-error card does not handle typescript */}
      <Card className="divide-y divide-gray-50 m-1">
        {isLoading && (
          <div className="flex justify-center p-4">
            <ArrowsLoop className="animate-spin" />
          </div>
        )}
        {!isLoading && organisationalUnits && organisationalUnits.map((organisationalUnit) => {
          const selected = selectedOrganisationalUnits.has(organisationalUnit.id)
          const customName = existingSelectedOrganisationalLookUpMap[organisationalUnit.id]?.name
          return (
            <ImporterLocationEntry
              key={organisationalUnit.id}
              id={organisationalUnit.id}
              name={customName ?? organisationalUnit.name}
              defaultName={organisationalUnit.name}
              brins={organisationalUnit.brins}
              selected={selected}
              onToggle={() => {
                setSelectedOrganisationalUnits((curr) => {
                  touched.current = true
                  const newData = new Map(curr)
                  if (selected) {
                    newData.delete(organisationalUnit.id)
                  } else {
                    newData.set(organisationalUnit.id, organisationalUnit)
                  }
                  onUpdateSelectedOrganisationalUnits(Array.from(newData.values()))
                  return newData
                })
              }}
              onSubmit={(ev) => {
                touched.current = true
                setSelectedOrganisationalUnits((curr) => {
                  const newData = new Map(curr)
                  newData.set(organisationalUnit.id, {
                    ...organisationalUnit,
                    name: ev.name,
                  })

                  onUpdateSelectedOrganisationalUnits(Array.from(newData.values()))
                  return newData
                })
              }}
              onPreventSubmit={() => {
              }}
              keyAlreadyExists={false}
              nameAlreadyExists={false}
            />
          )
        })}
      </Card>
    </div>
  )
}
